import { apiClient } from '../../../core/api/client';
import {
  CategoriesResponse,
  SpecialtiesResponse,
  ExpertiseResponse,
  Category,
  Specialty,
  Expertise,
} from '../types';

/**
 * Get all active categories
 * 
 * Retrieves top-level provider classifications for filtering.
 * Categories represent broad service types such as:
 * - Home Healthcare
 * - Legal Services
 * - Financial Services
 * - Memory Care
 * 
 * Results are sorted by displayOrder for consistent presentation.
 * Used to populate category filter dropdown in provider directory.
 * 
 * @returns List of all active categories
 */
export const getCategories = async (): Promise<CategoriesResponse> => {
  const response = await apiClient.get<CategoriesResponse>('/taxonomy/categories');
  return response.data;
};

/**
 * Get all active specialties with optional category filtering
 * 
 * Retrieves list of all active provider specialties for filtering.
 * Specialties represent specific professional roles such as:
 * - Geriatric Care Manager
 * - Elder Law Attorney
 * - Hospice Nurse
 * - Estate Planning Attorney
 * 
 * Can be filtered by categoryId to show specialties for a specific category.
 * Results sorted by displayOrder within each category.
 * 
 * Note: Large dataset (50-200+ items) - consider virtual scrolling in UI.
 * 
 * @param categoryId - Optional category ID to filter specialties
 * @returns List of active specialties, optionally filtered by category
 */
export const getSpecialties = async (
  categoryId?: string
): Promise<SpecialtiesResponse> => {
  const response = await apiClient.get<SpecialtiesResponse>(
    '/taxonomy/specialties',
    {
      params: categoryId ? { categoryId } : undefined,
    }
  );
  return response.data;
};

/**
 * Get all active expertise
 * 
 * Retrieves list of all active areas of expertise for filtering.
 * Expertise represents specific skills or knowledge areas such as:
 * - Dementia Care
 * - Estate Planning
 * - Medicare Navigation
 * - Family Counseling
 * 
 * Results sorted by displayOrder for consistent presentation.
 * 
 * Note: Large dataset (50-200+ items) - consider virtual scrolling in UI.
 * 
 * @returns List of all active expertise areas
 */
export const getExpertise = async (): Promise<ExpertiseResponse> => {
  const response = await apiClient.get<ExpertiseResponse>('/taxonomy/expertise');
  return response.data;
};

/**
 * Group specialties by category
 * 
 * Helper function to organize specialties into groups by their parent category.
 * Useful for rendering grouped/hierarchical dropdowns in the UI.
 * 
 * @param specialties - List of specialties to group
 * @returns Map of category ID to specialties in that category
 */
export const groupSpecialtiesByCategory = (
  specialties: Specialty[]
): Map<string, Specialty[]> => {
  const grouped = new Map<string, Specialty[]>();

  specialties.forEach((specialty) => {
    if (specialty.categoryId) {
      const existing = grouped.get(specialty.categoryId) || [];
      grouped.set(specialty.categoryId, [...existing, specialty]);
    }
  });

  return grouped;
};

/**
 * Get specialties grouped by category with category details
 * 
 * Helper function to fetch specialties and organize them by category,
 * including full category information for each group.
 * 
 * @returns Array of category groups with their specialties
 */
export const getSpecialtiesGroupedByCategory = async (): Promise<
  Array<{
    category: { id: string; name: string } | null;
    specialties: Specialty[];
  }>
> => {
  const { specialties } = await getSpecialties();
  const grouped = groupSpecialtiesByCategory(specialties);

  // Convert Map to array with category info
  const groups = Array.from(grouped.entries()).map(
    ([_, categorySpecialties]) => ({
      category: categorySpecialties[0]?.category || null,
      specialties: categorySpecialties,
    })
  );

  // Sort groups by category name
  groups.sort((a, b) => {
    if (!a.category || !b.category) return 0;
    return a.category.name.localeCompare(b.category.name);
  });

  return groups;
};

/**
 * Search categories by name
 * 
 * Helper function to filter categories by search term (case-insensitive).
 * Useful for searchable dropdown implementations.
 * 
 * @param categories - List of categories to search
 * @param searchTerm - Search term to match against category names
 * @returns Filtered list of categories matching the search term
 */
export const searchCategories = (
  categories: Category[],
  searchTerm: string
): Category[] => {
  if (!searchTerm.trim()) return categories;
  
  const term = searchTerm.toLowerCase();
  return categories.filter((category) =>
    category.name.toLowerCase().includes(term)
  );
};

/**
 * Search specialties by name
 * 
 * Helper function to filter specialties by search term (case-insensitive).
 * Useful for searchable dropdown implementations with virtual scrolling.
 * 
 * @param specialties - List of specialties to search
 * @param searchTerm - Search term to match against specialty names
 * @returns Filtered list of specialties matching the search term
 */
export const searchSpecialties = (
  specialties: Specialty[],
  searchTerm: string
): Specialty[] => {
  if (!searchTerm.trim()) return specialties;
  
  const term = searchTerm.toLowerCase();
  return specialties.filter((specialty) =>
    specialty.name.toLowerCase().includes(term)
  );
};

/**
 * Search expertise by name
 * 
 * Helper function to filter expertise by search term (case-insensitive).
 * Useful for searchable dropdown implementations with virtual scrolling.
 * 
 * @param expertise - List of expertise to search
 * @param searchTerm - Search term to match against expertise names
 * @returns Filtered list of expertise matching the search term
 */
export const searchExpertise = (
  expertise: Expertise[],
  searchTerm: string
): Expertise[] => {
  if (!searchTerm.trim()) return expertise;
  
  const term = searchTerm.toLowerCase();
  return expertise.filter((item) =>
    item.name.toLowerCase().includes(term)
  );
};

